import { useEffect, useState } from "react";
import { api, errToast, brl, dateBr } from "@/lib/api";
import { invalidateFinanceCaches } from "@/lib/cachedApi";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Plus, Building2, Trash2, Pencil, Loader2, Search } from "lucide-react";
import { toast } from "sonner";

const UFS = [
  "AC","AL","AP","AM","BA","CE","DF","ES","GO","MA","MT","MS","MG",
  "PA","PB","PR","PE","PI","RJ","RN","RS","RO","RR","SC","SP","SE","TO",
];

const EMPTY_EDIT = { name: "", address: "", number: "", city: "", state: "", postal_code: "", phone: "" };
const EMPTY_NEW = { name: "", kind: "filial", cnpj: "", city: "", state: "", emitter_id: "", accept: false };

export default function BranchSwitcher() {
  const [branches, setBranches] = useState([]);
  const [active, setActive] = useState(localStorage.getItem("oplyx_branch") || "");
  const [addOpen, setAddOpen] = useState(false);
  const [nova, setNova] = useState(EMPTY_NEW);
  // Preço e regras da filial nova (GET /branches/offer), carregados ao abrir.
  const [offer, setOffer] = useState(null);
  // Dados da Receita do CNPJ digitado (GET /public/cnpj): razão social e endereço.
  const [receita, setReceita] = useState(null);
  const [buscando, setBuscando] = useState(false);
  const [editBranch, setEditBranch] = useState(null);
  const [editForm, setEditForm] = useState(EMPTY_EDIT);
  const [saving, setSaving] = useState(false);
  const [cepLoading, setCepLoading] = useState(false);
  // Empresas emissoras do tenant. Com mais de uma, cada filial escolhe a sua.
  const [emitters, setEmitters] = useState([]);
  const [editEmitter, setEditEmitter] = useState("");

  const load = async () => {
    const { data } = await api.get("/branches");
    setBranches(data);
    if (!active && data[0]) { setActive(data[0].id); localStorage.setItem("oplyx_branch", data[0].id); }
    api.get("/fiscal/companies")
      .then(r => setEmitters((r.data.emitters || []).map(s => s.emitter).filter(Boolean)))
      .catch(() => setEmitters([]));
  };
  const multiEmitter = emitters.length > 1;
  const emitterOf = (b) => emitters.find(e => e.id === b.fiscal_emitter_id);
  const branchLabel = (b) => {
    if (!multiEmitter) return b.name;
    const em = emitterOf(b);
    return em ? `${b.name} · ${em.nome_fantasia || em.razao_social}` : `${b.name} · sem empresa emissora`;
  };
  useEffect(() => { load(); }, []);

  const change = async (id) => {
    await api.post(`/branches/${id}/activate`);
    localStorage.setItem("oplyx_branch", id);
    setActive(id);
    // O cache do painel não é por filial: sem limpar, a filial nova abria com
    // os números da anterior até o cache vencer.
    invalidateFinanceCaches();
    window.location.reload();
  };

  const openAdd = async (o) => {
    setAddOpen(o);
    if (!o) return;
    setNova(EMPTY_NEW);
    setOffer(null);
    setReceita(null);
    try { setOffer((await api.get("/branches/offer")).data); } catch (err) { errToast(err); }
  };

  const onCnpj = async (valor) => {
    setNova(n => ({ ...n, cnpj: valor }));
    const d = valor.replace(/\D/g, "");
    setReceita(null);
    if (d.length !== 14) return;
    if (offer?.raiz && d.slice(0, 8) !== offer.raiz) {
      setReceita({ erro: "Esse CNPJ não é filial desta empresa: o CNPJ base (os 8 primeiros dígitos) é diferente do da matriz." });
      return;
    }
    setBuscando(true);
    try {
      const { data } = await api.get(`/public/cnpj/${d}`);
      if (data.situacao_cadastral && data.situacao_cadastral.toUpperCase() !== "ATIVA") {
        setReceita({ erro: `CNPJ com situação ${data.situacao_cadastral} na Receita Federal.` });
        return;
      }
      setReceita(data);
      setNova(n => ({ ...n, city: data.municipio || n.city, state: data.uf || n.state,
                      name: n.name || data.nome_fantasia || "" }));
    } catch (err) {
      // Receita fora do ar: segue com cidade e UF digitadas.
      setReceita({ falhou: true, erro: err?.response?.status === 404 ? "CNPJ não encontrado na Receita Federal." : "" });
    } finally {
      setBuscando(false);
    }
  };

  const add = async (e) => {
    e.preventDefault();
    if (!nova.name.trim()) return;
    setSaving(true);
    try {
      const body = { name: nova.name, kind: nova.kind, accept_price: nova.accept };
      if (nova.kind === "filial") Object.assign(body, { cnpj: nova.cnpj, city: nova.city, state: nova.state });
      if (nova.kind === "ponto" && nova.emitter_id) body.emitter_id = nova.emitter_id;
      const { data } = await api.post("/branches", body);
      toast.success("Filial criada");
      setAddOpen(false);
      await load();
      // Filial com CNPJ próprio: o emissor dela é cadastrado na mesma tela da matriz.
      if (data.next) { window.location.href = data.next; return; }
      change(data.id);
    } catch (err) {
      errToast(err, "Erro ao criar a filial");
    } finally {
      setSaving(false);
    }
  };

  const del = async (id) => {
    if (branches.length <= 1) return toast.error("Deve existir ao menos 1 filial");
    if (!window.confirm("Desativar esta filial? O histórico dela fica guardado e, se ela é cobrada, sai da próxima mensalidade.")) return;
    try {
      await api.delete(`/branches/${id}`);
      toast.success("Filial desativada");
      await load();
    } catch (err) {
      errToast(err, "Erro ao desativar");
    }
  };

  const openEdit = (b) => {
    setEditBranch(b);
    setEditForm({
      name:        b.name        || "",
      address:     b.address     || "",
      number:      b.number      || "",
      city:        b.city        || "",
      state:       b.state       || "",
      postal_code: b.postal_code || "",
      phone:       b.phone       || "",
    });
    setEditEmitter(b.fiscal_emitter_id || "");
  };

  const lookupCep = async () => {
    const digits = editForm.postal_code.replace(/\D/g, "");
    if (digits.length !== 8) return toast.error("CEP deve ter 8 dígitos");
    setCepLoading(true);
    try {
      const res = await fetch(`https://brasilapi.com.br/api/cep/v2/${digits}`);
      if (!res.ok) throw new Error();
      const d = await res.json();
      setEditForm(f => ({
        ...f,
        address:     d.street       || f.address,
        city:        d.city         || f.city,
        state:       d.state        || f.state,
        postal_code: d.cep          || f.postal_code,
      }));
      toast.success("Endereço preenchido pelo CEP");
    } catch {
      toast.error("CEP não encontrado");
    } finally {
      setCepLoading(false);
    }
  };

  const saveEdit = async (e) => {
    e.preventDefault();
    if (!editForm.name.trim()) return toast.error("Nome obrigatório");
    setSaving(true);
    try {
      const payload = Object.fromEntries(
        Object.entries(editForm).filter(([, v]) => v !== "")
      );
      await api.patch(`/branches/${editBranch.id}`, payload);
      // Depois do endereço: a filial só pode usar o CNPJ de uma empresa da
      // mesma cidade (o backend confere).
      if (multiEmitter && editEmitter && editEmitter !== (editBranch.fiscal_emitter_id || "")) {
        await api.put(`/branches/${editBranch.id}/fiscal-emitter`, { emitter_id: editEmitter });
      }
      toast.success("Filial atualizada");
      setEditBranch(null);
      await load();
    } catch (err) {
      errToast(err, "Erro ao salvar");
    } finally {
      setSaving(false);
    }
  };

  const ef = (field) => (e) => setEditForm(f => ({ ...f, [field]: e.target.value }));

  return (
    <div className="px-4 pb-3 space-y-2">
      <div className="flex items-center gap-2 text-xs text-muted-foreground uppercase tracking-wider px-2">
        <Building2 className="w-3 h-3" /> Filial
      </div>
      <div className="flex gap-1">
        <Select value={active} onValueChange={change} data-testid="branch-select">
          <SelectTrigger className="flex-1 h-9"><SelectValue placeholder="Selecionar" /></SelectTrigger>
          <SelectContent>
            {branches.map(b => (
              <SelectItem key={b.id} value={b.id} data-testid={`branch-option-${b.id}`}>{branchLabel(b)}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        {/* Edit active branch */}
        {active && (() => {
          const activeBranch = branches.find(b => b.id === active);
          return activeBranch ? (
            <Button
              size="icon" variant="outline" className="h-9 w-9"
              onClick={() => openEdit(activeBranch)}
              data-testid="edit-branch-btn"
              title="Editar filial"
            >
              <Pencil className="w-4 h-4" />
            </Button>
          ) : null;
        })()}

        {/* Add branch */}
        <Dialog open={addOpen} onOpenChange={openAdd}>
          <DialogTrigger asChild>
            <Button size="icon" variant="outline" className="h-9 w-9" data-testid="add-branch-btn">
              <Plus className="w-4 h-4" />
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-md">
            <DialogHeader><DialogTitle>Nova filial</DialogTitle></DialogHeader>
            {!offer ? (
              <div className="flex justify-center py-6"><Loader2 className="w-5 h-5 animate-spin" /></div>
            ) : !offer.allowed ? (
              <p className="text-sm" data-testid="branch-upgrade-msg">
                Filiais adicionais fazem parte do plano Full. Fale com a OPLYX para mudar de plano.
              </p>
            ) : offer.limit_reached ? (
              <p className="text-sm">Sua conta chegou ao limite de {offer.max_branches} filiais. Fale com a OPLYX para ampliar.</p>
            ) : (
            <form onSubmit={add} className="space-y-4">
              <div>
                <Label>Nome</Label>
                <Input required value={nova.name} onChange={e => setNova(n => ({ ...n, name: e.target.value }))}
                  placeholder="Ex: Unidade Centro" data-testid="branch-name-input" />
              </div>
              <div className="space-y-2">
                <Label>Tipo</Label>
                {[["filial", "Filial em outro endereço", "Tem CNPJ próprio, com o mesmo CNPJ base da matriz (os 8 primeiros dígitos)."],
                  ["ponto", "Ponto de venda no mesmo endereço", "Outro caixa ou quiosque no endereço de uma loja que já emite. Usa o mesmo CNPJ."]]
                  .map(([k, t, d]) => (
                  <label key={k} className={`flex gap-2 rounded-lg border p-3 cursor-pointer ${nova.kind === k ? "border-primary" : ""}`}>
                    <input type="radio" name="branch-kind" checked={nova.kind === k}
                      onChange={() => setNova(n => ({ ...n, kind: k }))} data-testid={`branch-kind-${k}`} />
                    <span><span className="block text-sm font-medium">{t}</span>
                      <span className="block text-xs text-muted-foreground">{d}</span></span>
                  </label>
                ))}
              </div>
              {nova.kind === "filial" ? (
                <div className="space-y-3">
                  <div>
                    <Label>CNPJ da filial</Label>
                    <Input required value={nova.cnpj} onChange={e => onCnpj(e.target.value)}
                      placeholder={offer.raiz ? `${offer.raiz.slice(0, 2)}.${offer.raiz.slice(2, 5)}.${offer.raiz.slice(5, 8)}/0002-00` : "00.000.000/0002-00"}
                      data-testid="branch-cnpj-input" />
                  </div>
                  {buscando && (
                    <div className="flex items-center gap-2 text-xs text-muted-foreground">
                      <Loader2 className="w-3 h-3 animate-spin" /> Consultando a Receita Federal
                    </div>
                  )}
                  {receita?.erro && (
                    <p className="text-sm text-rose-700 dark:text-rose-300" data-testid="branch-cnpj-error">{receita.erro}</p>
                  )}
                  {receita?.razao_social && (
                    <div className="rounded-lg border p-3 text-sm space-y-1" data-testid="branch-receita">
                      <p className="font-medium">{receita.razao_social}</p>
                      {receita.nome_fantasia && <p className="text-xs text-muted-foreground">{receita.nome_fantasia}</p>}
                      <p className="text-xs text-muted-foreground">
                        {[receita.logradouro, receita.numero, receita.complemento, receita.bairro].filter(Boolean).join(", ")}
                        {" · "}{receita.municipio}/{receita.uf}{receita.cep ? ` · CEP ${receita.cep}` : ""}
                      </p>
                    </div>
                  )}
                  {receita?.falhou && !receita?.erro && (
                    <div className="grid grid-cols-3 gap-2">
                      <div className="col-span-2">
                        <Label>Cidade</Label>
                        <Input required value={nova.city} onChange={e => setNova(n => ({ ...n, city: e.target.value }))}
                          data-testid="branch-city-input" />
                      </div>
                      <div>
                        <Label>UF</Label>
                        <Select value={nova.state} onValueChange={v => setNova(n => ({ ...n, state: v }))}>
                          <SelectTrigger data-testid="branch-state-select"><SelectValue placeholder="UF" /></SelectTrigger>
                          <SelectContent>{UFS.map(u => <SelectItem key={u} value={u}>{u}</SelectItem>)}</SelectContent>
                        </Select>
                      </div>
                    </div>
                  )}
                  <p className="text-xs text-muted-foreground">
                    Depois de criar, você confere os dados da filial na Configuração fiscal e informa a Inscrição
                    Estadual e o certificado, do mesmo jeito que fez com a matriz.
                  </p>
                </div>
              ) : offer.emitters.length > 1 ? (
                <div>
                  <Label>Endereço de qual loja?</Label>
                  <Select value={nova.emitter_id} onValueChange={v => setNova(n => ({ ...n, emitter_id: v }))}>
                    <SelectTrigger data-testid="branch-emitter-select"><SelectValue placeholder="Escolha" /></SelectTrigger>
                    <SelectContent>
                      {offer.emitters.map(e => (
                        <SelectItem key={e.id} value={e.id}>{e.label}{e.city ? ` (${e.city}/${e.uf})` : ""}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              ) : null}
              {offer.price_month > 0 && (
                <div className="rounded-lg border border-amber-300 dark:border-amber-700/40 bg-amber-50 dark:bg-amber-900/20 p-3 space-y-2">
                  <p className="text-sm text-amber-800 dark:text-amber-200">
                    Filial adicional: <b>{brl(offer.price_month)}/mês</b>, somado à sua mensalidade, no mesmo
                    vencimento{offer.next_due ? `, a partir da cobrança de ${dateBr(offer.next_due)}` : ", a partir da próxima cobrança"}.
                  </p>
                  {offer.value_after && (
                    <p className="text-xs text-amber-700/80 dark:text-amber-300/70">
                      Sua mensalidade passa de {brl(offer.value_now)} para {brl(offer.value_after)}.
                    </p>
                  )}
                  <label className="flex items-start gap-2 text-sm text-amber-800 dark:text-amber-200">
                    <input type="checkbox" className="mt-1" checked={nova.accept}
                      onChange={e => setNova(n => ({ ...n, accept: e.target.checked }))} data-testid="branch-accept-price" />
                    Concordo com o valor adicional, conforme a Cláusula 4ª dos Termos de Uso.
                  </label>
                </div>
              )}
              <Button type="submit" className="w-full rounded-full" data-testid="branch-save-btn"
                disabled={saving || buscando || (offer.price_month > 0 && !nova.accept)
                  || (nova.kind === "filial" && (!!receita?.erro || (!receita?.razao_social && !receita?.falhou)))}>
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Criar"}
              </Button>
            </form>
            )}
            {branches.length > 1 && (
              <div className="mt-4 pt-4 border-t space-y-1">
                <div className="text-xs text-muted-foreground">Desativar filial:</div>
                {branches.map(b => (
                  <div key={b.id} className="flex items-center justify-between text-sm">
                    <span>{b.name}</span>
                    <Button size="icon" variant="ghost" onClick={() => del(b.id)} data-testid={`branch-deactivate-${b.id}`}>
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>

      {/* Edit branch dialog */}
      <Dialog open={!!editBranch} onOpenChange={(o) => { if (!o) setEditBranch(null); }}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Editar filial</DialogTitle></DialogHeader>
          <form onSubmit={saveEdit} className="space-y-3">
            <div>
              <Label>Nome da filial</Label>
              <Input value={editForm.name} onChange={ef("name")} placeholder="Ex: Unidade Centro"
                data-testid="edit-branch-name" />
            </div>

            {/* CEP + UF row */}
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label>CEP</Label>
                <div className="flex gap-1">
                  <Input
                    value={editForm.postal_code} onChange={ef("postal_code")}
                    placeholder="00000-000" maxLength={9}
                    data-testid="edit-branch-cep"
                    onBlur={() => editForm.postal_code.replace(/\D/g, "").length === 8 && lookupCep()}
                  />
                  <Button type="button" size="icon" variant="outline" className="shrink-0"
                    onClick={lookupCep} disabled={cepLoading} title="Buscar CEP">
                    {cepLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                  </Button>
                </div>
              </div>
              <div>
                <Label>Estado (UF)</Label>
                <Select value={editForm.state} onValueChange={v => setEditForm(f => ({ ...f, state: v }))}>
                  <SelectTrigger data-testid="edit-branch-state">
                    <SelectValue placeholder="UF" />
                  </SelectTrigger>
                  <SelectContent>
                    {UFS.map(uf => <SelectItem key={uf} value={uf}>{uf}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Address + number row */}
            <div className="grid grid-cols-3 gap-2">
              <div className="col-span-2">
                <Label>Endereço</Label>
                <Input value={editForm.address} onChange={ef("address")}
                  placeholder="Rua, Av., etc." data-testid="edit-branch-address" />
              </div>
              <div>
                <Label>Número</Label>
                <Input value={editForm.number} onChange={ef("number")}
                  placeholder="123" data-testid="edit-branch-number" />
              </div>
            </div>

            <div>
              <Label>Cidade</Label>
              <Input value={editForm.city} onChange={ef("city")}
                placeholder="São Paulo" data-testid="edit-branch-city" />
            </div>

            {multiEmitter && (
              <div>
                <Label>Empresa emissora desta filial</Label>
                <Select value={editEmitter} onValueChange={setEditEmitter}>
                  <SelectTrigger data-testid="edit-branch-emitter">
                    <SelectValue placeholder="Escolha a empresa" />
                  </SelectTrigger>
                  <SelectContent>
                    {emitters.map(em => (
                      <SelectItem key={em.id} value={em.id}>
                        {em.razao_social} · {em.cnpj}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground mt-1">
                  As notas desta filial saem com o CNPJ, a IE e o certificado desta empresa.
                  A troca vale para as vendas novas.
                </p>
              </div>
            )}

            <div>
              <Label>Telefone</Label>
              <Input value={editForm.phone} onChange={ef("phone")}
                placeholder="(11) 99999-0000" data-testid="edit-branch-phone" />
            </div>

            <Button type="submit" className="w-full rounded-full" disabled={saving}
              data-testid="edit-branch-save">
              {saving ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Salvando…</> : "Salvar"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
